import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AIIcon,
  TextFields as TextFieldsIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { emailsApi, signaturenApi, textbausteineApi, llmApi, dokumenteApi, adressenApi } from '../services/api';
import type { Email, EmailSignatur, Textbaustein, Adresse } from '../types/api';
import { Person as PersonIcon, Business as BusinessIcon } from '@mui/icons-material';
import RichTextEditor, { RichTextEditorHandle } from '../components/RichTextEditor';
import VoiceInputButton from '../components/VoiceInputButton';
import { marked } from 'marked';

// Helper function to convert newlines to HTML
const convertNewlinesToHtml = (text: string): string => {
  if (!text) return '';
  const hasNewlines = text.includes('\n');
  const hasParagraphTags = text.includes('<p>') && text.includes('</p>');
  if (hasParagraphTags && !hasNewlines) return text;
  if (hasNewlines) {
    const lines = text.split('\n');
    let html = '';
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) {
        if (i < lines.length - 1) html += '<p></p>';
      } else if (line.startsWith('<') && (line.includes('</') || line.endsWith('>'))) {
        html += line;
      } else {
        html += '<p>' + line + '</p>';
      }
    }
    return html;
  }
  return '<p>' + text + '</p>';
};

// Interface für Anhänge
interface Attachment {
  name: string;
  size: number;
  dokumentId?: number;
  file?: File;
}

const EmailCompose: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNewEmail = !id || id === 'new';
  const emailId = isNewEmail ? null : parseInt(id);
  const mode = isNewEmail ? 'new' : (searchParams.get('mode') || 'reply'); // reply, forward, new

  // Query-Parameter für Dokument-Anhänge
  const dokumentIdParam = searchParams.get('dokumentId');
  const betreffParam = searchParams.get('betreff');

  const [originalEmail, setOriginalEmail] = useState<Email | null>(null);
  const [signaturen, setSignaturen] = useState<EmailSignatur[]>([]);
  const [textbausteine, setTextbausteine] = useState<Textbaustein[]>([]);
  const [adressen, setAdressen] = useState<Adresse[]>([]);
  const [selectedAdresse, setSelectedAdresse] = useState<Adresse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit fields
  const [empfaenger, setEmpfaenger] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [betreff, setBetreff] = useState('');
  const [inhalt, setInhalt] = useState('');
  const [signaturId, setSignaturId] = useState<number | undefined>(undefined);

  // Anhänge
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // AI
  const [llmGenerating, setLlmGenerating] = useState(false);

  // Editor ref
  const editorRef = useRef<RichTextEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  // Effekt für Dokument-Anhänge aus Query-Parameter
  useEffect(() => {
    const loadDocumentAttachment = async () => {
      if (dokumentIdParam) {
        try {
          const dokId = parseInt(dokumentIdParam);
          const response = await dokumenteApi.getById(dokId);
          const dokument = response.data;

          // Dokument als Anhang hinzufügen
          setAttachments(prev => [...prev, {
            name: `${dokument.betreff || 'Dokument'}.docx`,
            size: 0, // Größe wird beim Senden ermittelt
            dokumentId: dokId,
          }]);

          // Betreff setzen wenn vorhanden
          if (betreffParam) {
            setBetreff(decodeURIComponent(betreffParam));
          }
        } catch (err) {
          console.error('Fehler beim Laden des Dokuments:', err);
        }
      }
    };

    if (!loading) {
      loadDocumentAttachment();
    }
  }, [dokumentIdParam, betreffParam, loading]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [signaturenResponse, textbausteineResponse, adressenResponse] = await Promise.all([
        signaturenApi.getAll(),
        textbausteineApi.getAll(),
        adressenApi.getAll({ take: 500 }),
      ]);

      setSignaturen(signaturenResponse.data);
      setTextbausteine(textbausteineResponse.data);
      setAdressen(adressenResponse.data);

      // Set default signature
      const defaultSig = signaturenResponse.data.find((s) => s.istStandard);
      if (defaultSig) {
        setSignaturId(defaultSig.signaturID);
      }

      // Load original email if replying/forwarding
      if (emailId) {
        const emailResponse = await emailsApi.getById(emailId);
        const email = emailResponse.data;
        setOriginalEmail(email);

        if (mode === 'reply') {
          setEmpfaenger(email.absenderEmail || '');
          setBetreff(email.betreffText.startsWith('Re:') ? email.betreffText : `Re: ${email.betreffText}`);

          // Prüfe ob ein KI-Antwortvorschlag in localStorage vorhanden ist
          const aiSuggestion = localStorage.getItem('emailAiSuggestion');
          if (aiSuggestion) {
            // Konvertiere den Text in HTML-Format für den Editor
            setInhalt(convertNewlinesToHtml(aiSuggestion));
            // Entferne den Vorschlag aus localStorage (einmalige Verwendung)
            localStorage.removeItem('emailAiSuggestion');
          }
        } else if (mode === 'forward') {
          setBetreff(email.betreffText.startsWith('Fwd:') ? email.betreffText : `Fwd: ${email.betreffText}`);
          // Add original message to content
          const forwardHeader = `\n\n---------- Weitergeleitete Nachricht ----------\nVon: ${email.absenderAnzeige}\nDatum: ${new Date(email.empfangenAm).toLocaleString('de-DE')}\nBetreff: ${email.betreffText}\n\n`;
          setInhalt(convertNewlinesToHtml(forwardHeader + (email.textInhalt || '')));
        }
      }
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const convertMarkdownToHtml = (markdown: string): string => {
    try {
      const lines = markdown.split('\n');
      const processedLines = lines.map((line) => {
        if (line.trim() === '') return '<br>';
        return marked.parseInline(line) as string;
      });
      return processedLines.join('<br>');
    } catch (error) {
      return markdown;
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: Attachment[] = Array.from(files).map(file => ({
      name: file.name,
      size: file.size,
      file: file,
    }));

    setAttachments(prev => [...prev, ...newAttachments]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    if (!empfaenger.trim()) {
      alert('Bitte geben Sie einen Empfänger ein.');
      return;
    }

    if (!inhalt.trim()) {
      alert('Bitte geben Sie einen Nachrichtentext ein.');
      return;
    }

    if (mode === 'new' && !betreff.trim()) {
      alert('Bitte geben Sie einen Betreff ein.');
      return;
    }

    try {
      setSending(true);

      if (emailId && mode === 'reply') {
        await emailsApi.sendReply(emailId, {
          antwortText: inhalt.replace(/<[^>]*>/g, ''),
          antwortHtml: inhalt,
          signaturID: signaturId,
        });
      } else if (emailId && mode === 'forward') {
        await emailsApi.forward(emailId, {
          empfaenger: empfaenger,
          nachricht: inhalt.replace(/<[^>]*>/g, ''),
          cc: cc || undefined,
          bcc: bcc || undefined,
          signaturID: signaturId,
        });
      } else if (mode === 'new') {
        // Send new email - mit oder ohne Anhänge
        if (attachments.length > 0) {
          // E-Mail mit Anhängen senden
          const formData = new FormData();
          formData.append('empfaenger', empfaenger);
          formData.append('betreff', betreff);
          formData.append('textInhalt', inhalt.replace(/<[^>]*>/g, ''));
          formData.append('htmlInhalt', inhalt);
          if (cc) formData.append('cc', cc);
          if (bcc) formData.append('bcc', bcc);
          if (signaturId) formData.append('signaturID', signaturId.toString());

          // Anhänge hinzufügen
          for (const attachment of attachments) {
            if (attachment.dokumentId) {
              // Dokument-ID als Anhang (Server lädt die Datei)
              formData.append('dokumentIds', attachment.dokumentId.toString());
            } else if (attachment.file) {
              // Datei-Anhang
              formData.append('attachments', attachment.file, attachment.name);
            }
          }

          await emailsApi.sendNewWithAttachments(formData);
        } else {
          // E-Mail ohne Anhänge
          await emailsApi.sendNew({
            empfaenger: empfaenger,
            betreff: betreff,
            textInhalt: inhalt.replace(/<[^>]*>/g, ''),
            cc: cc || undefined,
            bcc: bcc || undefined,
            signaturID: signaturId,
          });
        }
      } else {
        alert('Ungültiger Modus.');
        return;
      }

      alert('E-Mail erfolgreich gesendet!');
      navigate('/emails');
    } catch (err: any) {
      alert('Fehler beim Senden: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleAIImprove = async () => {
    if (!inhalt) return;

    try {
      setLlmGenerating(true);
      const response = await llmApi.improveText({
        originalText: inhalt,
        verbesserungsziel: 'Verbessere und optimiere diesen E-Mail-Text',
      });
      const htmlContent = convertMarkdownToHtml(response.data.text);
      setInhalt(htmlContent);
    } catch (err: any) {
      alert('Fehler bei der KI-Verbesserung: ' + (err.response?.data?.message || err.message));
    } finally {
      setLlmGenerating(false);
    }
  };

  const handleInsertTextbaustein = async (textbaustein: Textbaustein | null) => {
    if (!textbaustein) return;

    try {
      const response = await textbausteineApi.getContent(textbaustein.bausteinID);
      const content = response.data.content || '';
      const htmlContent = convertNewlinesToHtml(content);

      if (editorRef.current) {
        editorRef.current.insertContent(htmlContent);
      } else {
        setInhalt((prev) => (prev ? prev + '<p></p>' + htmlContent : htmlContent));
      }
    } catch (err: any) {
      alert('Fehler beim Laden des Textbausteins: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSignatureChange = (sigId: number | undefined) => {
    setSignaturId(sigId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh" position="fixed" top={0} left={0}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', height: '100vh', p: 3, position: 'fixed', top: 0, left: 0 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/emails')}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  const pageTitle = mode === 'reply' ? 'E-Mail beantworten' : mode === 'forward' ? 'E-Mail weiterleiten' : 'Neue E-Mail';

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'fixed', top: 0, left: 0, backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 3,
          py: 2,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/emails')}>
            Zurück
          </Button>
          <Typography variant="h5">{pageTitle}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSend}
            disabled={sending || !empfaenger.trim() || (mode === 'new' && !betreff.trim())}
          >
            {sending ? 'Sende...' : 'Senden'}
          </Button>
        </Box>
      </Box>

      {/* Original Email Info */}
      {originalEmail && mode === 'reply' && (
        <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Antwort auf E-Mail von: {originalEmail.absenderAnzeige}
          </Typography>
          <Typography variant="body2">Betreff: {originalEmail.betreffText}</Typography>
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, width: '100%', overflow: 'hidden' }}>
        {/* Left Side: Editor */}
        <Box sx={{ flex: '1 1 75%', height: '100%', borderRight: '1px solid', borderColor: 'divider', minWidth: 0, overflow: 'auto' }}>
          <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Nachricht</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <VoiceInputButton
                  onTranscription={(text) => {
                    if (editorRef.current) {
                      editorRef.current.insertText(text);
                    } else {
                      setInhalt((prev) => prev ? prev + ' ' + text : text);
                    }
                  }}
                  size="small"
                />
                <Button
                  size="small"
                  startIcon={llmGenerating ? <CircularProgress size={16} /> : <AIIcon />}
                  onClick={handleAIImprove}
                  disabled={llmGenerating || !inhalt}
                >
                  {llmGenerating ? 'Verbessere...' : 'Mit KI verbessern'}
                </Button>
              </Box>
            </Box>

            <TextField
              fullWidth
              size="small"
              label="An"
              value={empfaenger}
              onChange={(e) => setEmpfaenger(e.target.value)}
              required
              sx={{ mb: 2 }}
              placeholder="empfaenger@beispiel.de"
            />

            <TextField
              fullWidth
              size="small"
              label="CC"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="cc@beispiel.de"
            />

            <TextField
              fullWidth
              size="small"
              label="BCC"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="bcc@beispiel.de"
            />

            <TextField
              fullWidth
              size="small"
              label="Betreff"
              value={betreff}
              onChange={(e) => setBetreff(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Anhänge */}
            {attachments.length > 0 && (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AttachFileIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">Anhänge ({attachments.length})</Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {attachments.map((attachment, index) => (
                    <Chip
                      key={index}
                      label={attachment.name}
                      size="small"
                      onDelete={() => {
                        setAttachments(prev => prev.filter((_, i) => i !== index));
                      }}
                      deleteIcon={<DeleteIcon />}
                      sx={{
                        maxWidth: '200px',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <RichTextEditor
                ref={editorRef}
                content={inhalt}
                onChange={setInhalt}
                minHeight="400px"
                maxHeight="none"
              />
            </Box>
          </Box>
        </Box>

        {/* Right Side: Sidebar */}
        <Box sx={{ flex: '1 1 25%', height: '100%', minWidth: 300, overflow: 'auto', backgroundColor: 'background.paper' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Einstellungen
            </Typography>

            {/* Signature Selection */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Signatur
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Signatur auswählen</InputLabel>
                  <Select
                    value={signaturId || ''}
                    label="Signatur auswählen"
                    onChange={(e) => handleSignatureChange(e.target.value ? Number(e.target.value) : undefined)}
                  >
                    <MenuItem value="">
                      <em>Keine Signatur</em>
                    </MenuItem>
                    {signaturen.map((sig) => (
                      <MenuItem key={sig.signaturID} value={sig.signaturID}>
                        {sig.name}
                        {sig.istStandard && ' (Standard)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            {/* Adresse zuordnen - nur bei neuen E-Mails */}
            {mode === 'new' && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Adresse zuordnen
                  </Typography>
                  <Autocomplete
                    options={adressen}
                    value={selectedAdresse}
                    getOptionLabel={(option) => {
                      const name = option.vollstaendigerName || '';
                      const firma = option.firma || '';
                      if (name && firma) return `${name} (${firma})`;
                      return name || firma || '';
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {option.firma ? (
                            <BusinessIcon fontSize="small" color="action" />
                          ) : (
                            <PersonIcon fontSize="small" color="action" />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {option.vollstaendigerName || option.firma}
                            </Typography>
                            {option.firma && option.vollstaendigerName && (
                              <Typography variant="caption" color="text.secondary">
                                {option.firma}
                              </Typography>
                            )}
                            {option.email && (
                              <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                                {option.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    onChange={(_, value) => {
                      setSelectedAdresse(value);
                      // Wenn Adresse eine E-Mail hat und Empfänger leer ist, setze sie
                      if (value?.email && !empfaenger) {
                        setEmpfaenger(value.email);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Adresse suchen..."
                      />
                    )}
                    fullWidth
                    isOptionEqualToValue={(option, value) => option.adresseID === value.adresseID}
                  />
                  {selectedAdresse && (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Zugeordnete Adresse:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedAdresse.vollstaendigerName || selectedAdresse.firma}
                      </Typography>
                      {selectedAdresse.firma && selectedAdresse.vollstaendigerName && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {selectedAdresse.firma}
                        </Typography>
                      )}
                      {selectedAdresse.email && (
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <Typography variant="caption" color="primary">
                            {selectedAdresse.email}
                          </Typography>
                          {empfaenger !== selectedAdresse.email && (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem' }}
                              onClick={() => setEmpfaenger(selectedAdresse.email || '')}
                            >
                              In "An" übernehmen
                            </Button>
                          )}
                        </Box>
                      )}
                      {selectedAdresse.strasse && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {selectedAdresse.strasse} {selectedAdresse.hausnummer}, {selectedAdresse.plz} {selectedAdresse.ort}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Anhänge hinzufügen */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Anhänge
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Datei anhängen
                </Button>
                {attachments.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {attachments.length} Datei(en) angehängt
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {attachments.map((attachment, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 0.5,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" noWrap sx={{ flex: 1, mr: 1 }}>
                            {attachment.name}
                          </Typography>
                          <DeleteIcon
                            fontSize="small"
                            sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                            onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Divider sx={{ mb: 2 }} />

            {/* Textbausteine */}
            <Typography variant="subtitle2" gutterBottom>
              Textbausteine
            </Typography>
            <Autocomplete
              options={textbausteine}
              getOptionLabel={(option) => option.bausteinName || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.bausteinName}
                    </Typography>
                    {option.kategorie && (
                      <Chip size="small" label={option.kategorie} sx={{ mr: 1, mt: 0.5 }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {option.kurzerText?.substring(0, 80)}
                      {option.kurzerText && option.kurzerText.length > 80 ? '...' : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              onChange={(_, value) => handleInsertTextbaustein(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Textbaustein suchen..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <TextFieldsIcon color="action" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
              clearOnBlur
              blurOnSelect
              value={null}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Wählen Sie einen Textbaustein aus, um ihn an der Cursor-Position einzufügen
            </Typography>

            {/* Original Email Preview */}
            {originalEmail && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Original E-Mail
                </Typography>
                <Card variant="outlined">
                  <CardContent sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Von: {originalEmail.absenderAnzeige}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Datum: {new Date(originalEmail.empfangenAm).toLocaleString('de-DE')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Betreff: {originalEmail.betreffText}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {originalEmail.textInhalt?.substring(0, 500)}
                      {originalEmail.textInhalt && originalEmail.textInhalt.length > 500 ? '...' : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        onChange={handleFileSelect}
      />
    </Box>
  );
};

export default EmailCompose;
